/**
 * NavGuardModal — custom-branded "Discard unsaved changes?" modal for
 * React-Router in-app navigation (v0.1.27 rewrite, Bill 2026-04-23).
 *
 * Replaces the v0.1.23 anchor-click-interception approach with React Router
 * v6.7+'s useBlocker hook. useBlocker catches ALL router navigations — Link
 * clicks, NavLink clicks, programmatic navigate() calls, history.back, etc.
 * — not just same-origin <a href> clicks, so no more holes where a
 * .useNavigate()-driven button bypassed the prompt.
 *
 * Dirty-flag source of truth: SessionContext.isDirty (aggregate of
 * useFormGuard registrations) ORed with the legacy activeCartId pin — both
 * count as "in-flight work." The body text adapts: if a cart is pinned we
 * mention the cart id; otherwise a generic message.
 *
 * The Teams "X" / window close path is handled separately in
 * SessionContext's registerBeforeUnloadHandler effect (system-level
 * confirm dialog, not customizable per Teams security constraints).
 *
 * A11y:
 *   - role="alertdialog" (stronger than plain dialog for destructive confirms)
 *   - aria-labelledby on the title, aria-describedby on the body
 *   - Focus is trapped inside the modal; Tab cycles
 *   - Escape = Cancel
 *   - Cancel button gets initial focus (safer default than Discard)
 *
 * Styling: all colors come from tokens.css theme variables so Light / Dark /
 * High-Contrast all inherit automatically. No hex literals.
 */
import { useCallback, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useBlocker } from 'react-router';
import { useSession } from '@/contexts/SessionContext';
import './NavGuardModal.css';

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function NavGuardModal(): JSX.Element | null {
  const { isDirty, activeCartId, clearAllDirty } = useSession();

  // Block ONLY when there's actual unsaved form state. A pinned resume cart
  // is server-persisted, so navigating away doesn't lose anything — guarding
  // on it produced false-positive "Discard?" prompts on every admin click
  // (Bill 2026-04-26).
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && currentLocation.pathname !== nextLocation.pathname,
  );

  const isOpen = blocker.state === 'blocked';
  const modalRef = useRef<HTMLDivElement | null>(null);
  const priorFocusRef = useRef<HTMLElement | null>(null);
  const cancelBtnRef = useRef<HTMLButtonElement | null>(null);
  const titleId = useId();
  const bodyId = useId();

  const handleCancel = useCallback((): void => {
    if (blocker.state === 'blocked') blocker.reset();
  }, [blocker]);

  const handleConfirm = useCallback((): void => {
    if (blocker.state !== 'blocked') return;
    clearAllDirty(); // release all form dirty flags so guards don't re-trigger
    blocker.proceed();
  }, [blocker, clearAllDirty]);

  // Focus management + ESC-to-cancel + focus trap while the modal is open.
  useEffect(() => {
    if (!isOpen) return;

    priorFocusRef.current = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Default focus to the Cancel button — destructive-first defaults are a
    // UX anti-pattern. Let the user confirm with an explicit action.
    window.setTimeout(() => cancelBtnRef.current?.focus(), 0);

    function onKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleCancel();
        return;
      }
      if (e.key === 'Tab' && modalRef.current) {
        const focusables = modalRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (!first || !last) return;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      priorFocusRef.current?.focus();
    };
  }, [isOpen, handleCancel]);

  if (!isOpen) return null;

  const bodyText = 'You have unsaved changes on this page. Leaving will discard them.';

  const overlay = (
    <div
      className="nav-guard__overlay"
      role="presentation"
      onClick={(e) => {
        // Click on scrim (not the dialog) = Cancel. Matches ModalShell behavior.
        if (e.target === e.currentTarget) handleCancel();
      }}
    >
      <div
        ref={modalRef}
        className="nav-guard__dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
      >
        <h2 id={titleId} className="nav-guard__title">
          Discard unsaved changes?
        </h2>
        <p id={bodyId} className="nav-guard__body">
          {bodyText}
        </p>
        <div className="nav-guard__footer">
          <button
            ref={cancelBtnRef}
            type="button"
            className="btn btn-ghost"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleConfirm}
          >
            Discard &amp; Leave
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}

export default NavGuardModal;
